import * as React from 'react'
import * as PropTypes from 'prop-types'
import { css, Rule } from 'glamor'
import * as warning_ from 'warning'
import { Portal } from 'react-portal'

import { withTheme, IWithThemeProps } from '../../theme'
import isMounted from '../../utils/is-mounted'

import getStyles from './styles'

const warning = warning_

const calculateTooltipPosition = (position: TooltipPosition, container: HTMLElement) => {
  const clientRect = container.getBoundingClientRect()

  let top
  let left

  switch (position) {
    case 'top':
      top = clientRect.top - 10
      left = clientRect.left + clientRect.width / 2
      break
    case 'right':
      top = clientRect.top + clientRect.height / 2
      left = clientRect.left + clientRect.width + 10
      break
    case 'bottom':
      top = clientRect.top + clientRect.height + 10
      left = clientRect.left + clientRect.width / 2
      break
    case 'left':
      top = clientRect.top + clientRect.height / 2
      left = clientRect.left - 10
      break
    default:
      throw new Error(`Unknown posiiton ${position}`)
  }

  return { top, left }
}

type TooltipPosition = 'top' | 'left' | 'right' | 'bottom'

const positions: TooltipPosition[] = ['top', 'left', 'right', 'bottom']

interface ITooltipProps {
  text: React.ReactNode
  storageKey?: string
  times?: number
  children?: React.ReactNode
  wrapper?: string
  position?: TooltipPosition
  style?: Rule
  wrapperStyle?: Rule
  fluid?: boolean
  runIfMounted: (fn: () => void) => void
}

interface ITooltipState {
  positioned: boolean
  visible: boolean
  times: number
  currentPosition: {
    top: number
    left: number
  } | null
  forcedPosition: TooltipPosition | null
}

export const Tooltip = withTheme(
  isMounted(
    class Tooltip extends React.Component<ITooltipProps & IWithThemeProps, ITooltipState> {
      static displayName = 'Tooltip'
      static propTypes = {
        storageKey: PropTypes.string,
        times: PropTypes.number,
        children: PropTypes.node,
        wrapper: PropTypes.string,
        position: PropTypes.oneOf(positions),
        text: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
        style: PropTypes.object,
        wrapperStyle: PropTypes.object,
        fluid: PropTypes.bool,
      }

      static defaultProps = {
        wrapper: 'span',
        position: 'top' as TooltipPosition,
      }

      constructor(props: ITooltipProps & IWithThemeProps) {
        super(props)

        warning(!props.storageKey === !props.times, 'Tooltip.times cannot be used without setting the storageKey prop')

        this.__key = props.storageKey && `@@${process.env.DOOER_PACKAGE_NAME}/tooltip-${props.storageKey}`

        this.state = {
          // Positioned denotes whether or not the element has been positioned,
          // while visible tells if the tooltip is also to be shown. This is used
          // for animation purposes.
          positioned: false,
          visible: false,
          currentPosition: null,
          forcedPosition: null,
          times: this.__key ? parseInt(window.localStorage.getItem(this.__key) || '0', 10) : 0,
        }

        this.content = React.createRef()
        this.tooltip = React.createRef()
      }

      componentDidUpdate(_: ITooltipProps & IWithThemeProps, prevState: ITooltipState) {
        const { runIfMounted, position } = this.props
        const { positioned, currentPosition, forcedPosition } = this.state

        if (positioned && !currentPosition) {
          runIfMounted(() =>
            this.setState({
              currentPosition:
                position && this.content.current ? calculateTooltipPosition(position, this.content.current) : null,
            })
          )
        } else if (
          positioned &&
          !prevState.currentPosition &&
          currentPosition &&
          this.tooltip.current &&
          !forcedPosition
        ) {
          const clientRect = this.tooltip.current.getBoundingClientRect()
          let forcePosition: TooltipPosition | null = null

          if (clientRect.top < 0) forcePosition = 'bottom'
          if (clientRect.left < 0) forcePosition = 'right'
          if (clientRect.top + clientRect.height > window.innerHeight) forcePosition = 'top'
          if (clientRect.left + clientRect.width > window.innerWidth) forcePosition = 'left'

          if (forcePosition) {
            runIfMounted(
              () =>
                forcePosition &&
                this.content.current &&
                this.setState({
                  currentPosition: calculateTooltipPosition(forcePosition, this.content.current),
                  forcedPosition: forcePosition,
                  positioned: false,
                })
            )
          } else {
            setTimeout(() => this.setState({ visible: true }))
          }
        } else if (currentPosition && !prevState.forcedPosition && forcedPosition) {
          runIfMounted(() => this.setState({ positioned: true }))
          setTimeout(() => this.setState({ visible: true }))
        }
      }

      __key?: string
      content: React.RefObject<HTMLElement>
      tooltip: React.RefObject<HTMLElement>

      render() {
        const { children, wrapper, text, wrapperStyle, style, times, fluid } = this.props

        const { positioned, currentPosition, forcedPosition, visible } = this.state

        const position = forcedPosition || this.props.position

        if (times && this.state.times >= times) return children

        const setIsPositioned = (isPositioned: boolean) =>
          this.setState(state => ({
            positioned: isPositioned,
            visible: !isPositioned ? false : state.visible,
            currentPosition: !isPositioned ? null : state.currentPosition,
            forcedPosition: !isPositioned ? null : state.forcedPosition,
          }))

        const { styles, tooltipSettings } = getStyles(this.props.theme)

        return wrapper
          ? React.createElement(
              wrapper,
              {
                ...css(styles.wrapper.base, wrapperStyle),
              },
              React.isValidElement(children)
                ? React.cloneElement<any>(children, {
                    ref: this.content,
                    onMouseOver: (event: React.MouseEvent) => {
                      setIsPositioned(true)
                      if (children.props.onMouseOver) {
                        children.props.onMouseOver(event)
                      }
                    },
                    onMouseLeave: (event: React.MouseEvent) => {
                      if (this.__key) {
                        try {
                          window.localStorage.setItem(this.__key, `${this.state.times + 1}`)
                        } catch (e) {
                          /**
                           * Safari Incognito throws QuotaExceededError when setting items
                           * in local storage. This catches any errors, but if the error
                           * was caused by something else than the quota being exceeded
                           * we'll throw the error so it can be propagated to services
                           * like Sentry.
                           */
                          if (e.name !== 'QuotaExceededError') throw e
                        }
                      }

                      this.setState({
                        visible: false,
                        times: this.state.times + 1,
                      })

                      // Wait for the tooltip to animate out before removing positioning
                      setTimeout(() => setIsPositioned(false), tooltipSettings.transitionTimeout)

                      if (children.props.onMouseLeave) {
                        children.props.onMouseLeave(event)
                      }
                    },
                  })
                : null,
              positioned && currentPosition && (
                <Portal>
                  <span
                    {...css(
                      styles.tooltip.base,
                      fluid && styles.tooltip.fluid,
                      position && styles.tooltip.fixedPositions[position].base,
                      position && visible && styles.tooltip.fixedPositions[position].visible,
                      currentPosition,
                      visible && styles.tooltip.visible,
                      style
                    )}
                    ref={this.tooltip}
                  >
                    {text}
                    <i {...css(styles.tooltipArrow.base, position && styles.tooltipArrow[position])} />
                  </span>
                </Portal>
              )
            )
          : null
      }
    }
  )
)
